import { useMemo, useCallback } from 'react';
// @mui
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
// types
import { VuonTrong } from 'src/types/vuontrong';
// components
import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import { ConfirmDialog } from 'src/components/custom-dialog';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import { useSnackbar } from 'src/components/snackbar';
// sections
import { STATUS_OPTIONS } from 'src/constants/status';
import useVuonTrong from './hooks/use-vuontrong';

// ----------------------------------------------------------------------

type Props = {
  row: VuonTrong;
  selected?: boolean;
  onEditRow: VoidFunction;
  onViewRow: VoidFunction;
  onSelectRow?: VoidFunction;
  onDeleteRow: VoidFunction;
};

export default function VuonTrongTableRow({
  row,
  selected,
  onEditRow,
  onViewRow,
  onSelectRow,
  onDeleteRow,
}: Props) {
  const { vuonTrong_ID, tenVuon, diaChi, viTri, trangThai } = row;
  const { enqueueSnackbar } = useSnackbar();
  const { deleteVuonTrong } = useVuonTrong();

  const statusColor = useMemo(() => {
    switch (trangThai) {
      case 1:
        return 'success';
      case 2:
        return 'warning';
      case 3:
        return 'error';
      default:
        return 'default';
    }
  }, [trangThai]);

  const confirm = useBoolean();
  const popover = usePopover();

  const handleDelete = useCallback(async () => {
    try {
      const success = await deleteVuonTrong(vuonTrong_ID);
      if (success) {
        enqueueSnackbar('Xóa vườn trồng thành công!', { variant: 'success' });
        onDeleteRow();
      } else {
        enqueueSnackbar('Xóa vườn trồng thất bại!', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error deleting garden:', error);
      enqueueSnackbar('Xóa vườn trồng thất bại!', { variant: 'error' });
    } finally {
      confirm.onFalse();
    }
  }, [vuonTrong_ID, deleteVuonTrong, onDeleteRow, enqueueSnackbar, confirm]);

  const renderPrimary = (
    <TableRow hover selected={selected}>
      {onSelectRow && (
        <TableCell padding="checkbox">
          <Checkbox checked={selected} onClick={onSelectRow} />
        </TableCell>
      )}

      <TableCell>
        <Box
          sx={{
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <ListItemText
            onClick={onEditRow}
            primary={tenVuon} 
            primaryTypographyProps={{ typography: 'body2', fontWeight: 'medium' }}
            secondary={viTri || ''}
            secondaryTypographyProps={{
              typography: 'caption',
              color: 'text.disabled',
              component: 'span',
            }}
          />
        </Box>
      </TableCell>

      <TableCell>
        <Box sx={{ maxWidth: 200 }}>
          <ListItemText
            primary={diaChi || '-'}
            primaryTypographyProps={{
              typography: 'body2',
              color: 'text.secondary',
              noWrap: true,
            }}
          />
        </Box>
      </TableCell>
      <TableCell>
        <Box sx={{ maxWidth: 200 }}>
          <ListItemText
            primary={viTri || '-'}
            primaryTypographyProps={{
              typography: 'body2',
              color: 'text.secondary',
              noWrap: true,
            }}
          />
        </Box>
      </TableCell>
      <TableCell align="center">
        <Label variant="soft" color={statusColor as any}>
          {STATUS_OPTIONS.find((o) => o.value === trangThai)?.label || '-'}
        </Label>
      </TableCell>

      <TableCell align="right" sx={{ px: 1, whiteSpace: 'nowrap' }}>
        <IconButton color={popover.open ? 'inherit' : 'default'} onClick={popover.onOpen}>
          <Iconify icon="eva:more-vertical-fill" />
        </IconButton>
      </TableCell>
    </TableRow>
  );

  return (
    <>
      {renderPrimary}

      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        arrow="right-top"
        sx={{ width: 140 }}
      >
        {/* <MenuItem
          onClick={() => { 
            onViewRow();
            popover.onClose();
          }}
        >
          <Iconify icon="solar:eye-bold" />
          Xem
        </MenuItem> */}

        <MenuItem
          onClick={() => {
            onEditRow();
            popover.onClose();
          }}
        >
          <Iconify icon="solar:pen-bold" />
          Sửa
        </MenuItem>

        {/* <MenuItem
          onClick={() => {
            confirm.onTrue(); 
            popover.onClose();
          }}
          sx={{ color: 'error.main' }}
        >
          <Iconify icon="solar:trash-bin-trash-bold" />
          Xóa
        </MenuItem> */}
      </CustomPopover>

      {/* <ConfirmDialog
        open={confirm.value}
        onClose={confirm.onFalse}
        title="Xóa vườn trồng"
        content={`Bạn có chắc chắn muốn xóa vườn "${tenVuon}"?`}
        action={
        <Button
          variant="contained"
          color="error"
          onClick={handleDelete}
        >
          Xóa
        </Button>
        }
      /> */}
    </>
  );
}
